"use client";
import { QuestInteractionHandler } from "@/app/games/(details)/QuestInteractionHandler";
import {
  GemsBadge,
  PerpetualRewardsBadge,
  PointsBadge,
} from "@/app/games/(details)/quest-badges";
import {
  type GameData,
  formatQuestNumber,
  getQuestIcon,
} from "@/app/games/(details)/types";
import { useGameDetails } from "@/app/games/(details)/useGameDetails";
import { ListItem, listItemTextVariants } from "@/components/ui/list";
import { Progress } from "@/components/ui/progress";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useGamePageV3Analytics } from "@/context/AnalyticsProvider/gamePageV3";
import { cn } from "@/lib/utils";
import { IterationCcw, Users } from "lucide-react";
import { Fragment } from "react";

export const Quests = ({ gameData }: { gameData: GameData }) => {
  const { track } = useGamePageV3Analytics();
  const {
    totalQuestsCompleted,
    incompleteQuests,
    allQuests,
    completeQuest,
    inProgressQuest,
    setInProgressQuest,
    onQuestClick,
  } = useGameDetails(gameData);

  // Calculate progress based on total rewards earned vs total possible gems
  const totalGemsEarned = allQuests.reduce((sum, quest) => {
    // For completed quests, multiply gems by completion count (for recurring quests)
    if (quest.completed && quest.completionCount && quest.completionCount > 0) {
      return sum + quest.gems * quest.completionCount;
    }
    return sum;
  }, 0);

  const totalPossibleGems = allQuests.reduce(
    (sum, quest) => sum + quest.gems,
    0,
  );

  // Ensure progress doesn't exceed 100% for recurring quests
  const progressPercentage =
    totalPossibleGems > 0
      ? Math.min((totalGemsEarned / totalPossibleGems) * 100, 100)
      : 0;

  return (
    <div className="w-full mt-8">
      <h1 className="font-suisse font-semibold text-2xl leading-8 text-neutral-50 text-center">
        {gameData.details.name} Quests
      </h1>

      <div className="flex items-center gap-5 mt-4">
        <Progress
          value={progressPercentage}
          className="bg-neutral-50/20 flex-1"
        />
        <GemsBadge
          text={`${totalGemsEarned} / ${Math.max(totalPossibleGems, totalGemsEarned)}`}
          completed={totalGemsEarned >= totalPossibleGems}
          className="bg-neutral-950/40"
        />
      </div>

      <div className="flex-1 h-px bg-foreground opacity-5 mt-4" />
      {allQuests.map((quest) => {
        const QuestIcon = getQuestIcon(quest.type, quest.categories ?? []);
        return (
          <Fragment key={quest.id}>
            <ListItem
              disabled={quest.completedToday}
              variant="transparent"
              size="md"
              className={cn(
                "px-3 py-2.5 hover:bg-white/5 transition-all duration-300",
                quest.completedToday && "opacity-50",
              )}
              onClick={() => {
                if (!quest.completedToday) {
                  if (gameData.details.game_id) {
                    track({
                      action: Action.PRESSED,
                      extras: {
                        id: "quest-click",
                        questId: quest.externalId,
                        control: "QuestItem",
                        controlType: ControlType.BUTTON,
                        gameId: gameData.details.game_id,
                        gameName: gameData.details.name,
                      },
                    });
                  }
                  onQuestClick(quest);
                }
              }}
            >
              <div className="flex items-start w-full gap-3">
                {/* Icon and Small Screen Badges */}
                <div className="flex flex-col items-center gap-2 shrink-0">
                  <span className="w-12 h-12 rounded border border-border bg-background/20 backdrop-blur-md flex items-center justify-center">
                    <QuestIcon className="w-5 h-5 text-foreground" />
                  </span>
                  {/* Badges beneath icon on small screens */}
                  <span className="flex flex-col gap-1 items-center sm:hidden">
                    <PointsBadge
                      points={quest.campaignPoints}
                      completed={quest.completed}
                      className="bg-neutral-950/40"
                    />
                    <GemsBadge
                      gems={quest.gems}
                      completed={quest.completed}
                      className="bg-neutral-950/40"
                    />
                  </span>
                </div>

                {/* Main Content */}
                <div className={listItemTextVariants()}>
                  <span className="font-suisse font-medium text-sm text-foreground leading-5 cursor-default line-clamp-2">
                    {quest.name}
                  </span>
                  <div className="font-suisse text-xs text-muted-foreground leading-4 cursor-default space-y-2">
                    {quest.caption && (
                      <p className="text-sm text-muted-foreground leading-5">
                        {quest.caption}
                      </p>
                    )}
                    {/* Perpetual Rewards Indicator */}
                    {quest.perpetualRewardsEligible && (
                      <PerpetualRewardsBadge />
                    )}
                    <div className="flex flex-wrap gap-2.5">
                      {quest.refreshPeriodHours !== null &&
                        quest.completionCount !== undefined &&
                        quest.completionCount > 0 && (
                          <div className="flex items-center gap-1">
                            <IterationCcw className="w-3 h-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">
                              {quest.completionCount} completion
                              {quest.completionCount !== 1 ? "s" : ""}
                            </span>
                          </div>
                        )}
                      {quest.participantCount && (
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {formatQuestNumber(quest.participantCount)}{" "}
                            participants
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Badges on larger screens */}
                <div className="hidden sm:flex flex-col items-end gap-1">
                  <PointsBadge
                    className="self-end bg-neutral-950/40"
                    points={quest.campaignPoints}
                    completed={quest.completed}
                  />
                  <GemsBadge
                    className="self-end bg-neutral-950/40"
                    gems={quest.gems}
                    completed={quest.completed}
                  />
                </div>
              </div>
            </ListItem>
            <div className="flex-1 h-px bg-foreground opacity-5" />
          </Fragment>
        );
      })}
      <QuestInteractionHandler
        game={gameData}
        completeQuest={async (quest) => {
          if (await completeQuest(quest)) {
            if (gameData.details.game_id) {
              track({
                action: Action.SUCCEEDED,
                extras: {
                  id: "quest-completed",
                  questId: quest.externalId,
                  gameId: gameData.details.game_id,
                  gameName: gameData.details.name,
                },
              });
            }
          } else {
            if (gameData.details.game_id) {
              track({
                action: Action.FAILED,
                extras: {
                  id: "quest-completed",
                  questId: quest.externalId,
                  gameId: gameData.details.game_id,
                  gameName: gameData.details.name,
                },
              });
            }
          }
        }}
        setInProgressQuest={setInProgressQuest}
        inProgressQuest={inProgressQuest}
      />
    </div>
  );
};
